import { useEffect, useRef, useState, type ReactNode } from "react";
import { Check, ShieldCheck } from "lucide-react";
import { AppFooter } from "@/components/shell/AppFooter";
import { MountSinaiLogo } from "@/components/shell/MountSinaiLogo";
import { useTheme } from "@/theme/ThemeContext";
import { cn } from "@/lib/utils";
import { rise } from "./rise";

// =============================================================================
// AuthLayout
//
// The frame every signed-out page shares — sign in, forgot password, reset
// password, invitation, magic link:
//
//   - Left pane: Royal × Mount Sinai wordmarks pinned to the top, the page's
//     content centred over a slow ambient light backdrop, footer at the bottom.
//   - Right pane (`hero`): the Royal Sienna at Mount Sinai, wiped in with a
//     rounded leading edge, then drifting slowly (Ken Burns). Pages that are
//     only passed through (the magic-link landing) leave it off.
//
// All motion lives in index.css under "Login page" and switches off under
// prefers-reduced-motion. Nothing here touches sign-in logic.
//
// Stacks vertically below 1024px so the form is the first thing on mobile.
// =============================================================================

export function AuthLayout({ hero = true, children }: { hero?: boolean; children: ReactNode }) {
  return (
    // overflow-x-clip: the card's glow reaches past the column on purpose.
    // On a phone that put it past the screen edge, which widened the page to
    // ~448px, so it could be dragged sideways. Clip (not hidden) so no
    // scroll container is created.
    <div
      className={cn(
        "relative isolate grid min-h-[100dvh] overflow-x-clip",
        hero && "lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]",
      )}
    >
      <AmbientBackdrop />
      <main
        id="main-content"
        className="relative flex flex-col px-6 py-8 sm:px-10 lg:px-14 lg:py-10"
      >
        <div className={cn("login-rise", !hero && "flex justify-center")} style={rise(0)}>
          <BrandRow />
        </div>

        <div className="flex flex-1 items-center justify-center py-8 lg:py-0">
          <div className="w-full max-w-[420px] 2xl:max-w-[500px]">{children}</div>
        </div>

        <div className="login-rise" style={rise(420)}>
          <AppFooter />
        </div>
      </main>
      {hero ? <HeroPane /> : null}
    </div>
  );
}

/** Heading block above the card: optional eyebrow chip, title, one line. */
export function AuthIntro({
  eyebrow,
  title,
  children,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
}) {
  return (
    <>
      {eyebrow ? <AuthEyebrow>{eyebrow}</AuthEyebrow> : null}
      <h1
        className="login-rise mb-2 text-3xl font-extrabold tracking-tight text-foreground sm:text-[2.25rem] sm:leading-[1.1] [text-wrap:balance] 2xl:mb-3 2xl:text-[2.5rem]"
        style={rise(110)}
      >
        {title}
      </h1>
      {children ? (
        <p
          className="login-rise mb-7 text-[15px] text-foreground/70 [text-wrap:balance] 2xl:mb-9 2xl:text-[17px]"
          style={rise(170)}
        >
          {children}
        </p>
      ) : null}
    </>
  );
}

/** Small pill above a heading ("Good morning", "You're invited"). */
export function AuthEyebrow({ children }: { children: ReactNode }) {
  return (
    <p
      className="login-rise mb-4 inline-flex items-center gap-2 rounded-full border border-border/70 bg-foreground/[0.04] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground"
      style={rise(60)}
    >
      <span className="relative flex h-2 w-2" aria-hidden="true">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/60 [animation-iteration-count:4]" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-gradient-to-br from-primary to-[hsl(var(--accent-2))]" />
      </span>
      {children}
    </p>
  );
}

/** The frosted glass card with its glow and travelling border light. */
export function AuthCard({ children }: { children: ReactNode }) {
  return (
    <div className="login-rise relative isolate" style={rise(240)}>
      <div aria-hidden="true" className="login-card-glow" />
      <div className="login-card p-6 sm:p-7 2xl:p-9">{children}</div>
    </div>
  );
}

/** Taller, softer inputs, and a glow on focus that composes with the shared
 *  Input's focus ring. AUTH_INPUT leaves room for an IconInput icon. */
export const AUTH_INPUT_PLAIN =
  "h-12 rounded-2xl bg-background/40 text-[15px] focus:shadow-[0_12px_32px_-14px_hsl(var(--primary)/0.7)] 2xl:h-14 2xl:text-base";
export const AUTH_INPUT = `${AUTH_INPUT_PLAIN} pl-10`;

export function IconInput({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="group relative">
      <span className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-muted-foreground transition-colors duration-300 group-focus-within:text-primary">
        {icon}
      </span>
      {children}
    </div>
  );
}

/** A form error that shakes once. Give it `key={attempt}` so a repeated
 *  error re-mounts and shakes again. */
export function FormAlert({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="login-shake rounded-xl border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm font-semibold text-destructive"
    >
      {children}
    </p>
  );
}

/** Live checks for the two password rules the form already enforces on
 *  submit. Feedback only — validation itself is unchanged. */
export function PasswordChecklist({ password, confirm }: { password: string; confirm: string }) {
  const rules = [
    { ok: password.length >= 8, label: "At least 8 characters" },
    { ok: password.length > 0 && password === confirm, label: "Passwords match" },
  ];
  return (
    <ul className="-mt-1 grid gap-1.5 text-xs">
      {rules.map((r) => (
        <li
          key={r.label}
          className={cn(
            "flex items-center gap-2 transition-colors duration-300",
            r.ok ? "font-semibold text-success" : "text-muted-foreground",
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              "flex h-4 w-4 items-center justify-center rounded-full border transition-colors duration-300",
              r.ok ? "border-success bg-success text-background" : "border-border",
            )}
          >
            {r.ok ? <Check className="h-2.5 w-2.5" strokeWidth={3.5} /> : null}
          </span>
          {r.label}
        </li>
      ))}
    </ul>
  );
}

/** A result screen inside the card: drawn mark, title, one line, action. */
export function AuthStatus({
  tone,
  title,
  children,
  action,
  quick,
}: {
  tone: StatusTone;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  /** Draw the mark fast, for screens that move on after 600ms. */
  quick?: boolean;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="login-pop flex flex-col items-center gap-4 px-2 py-4 text-center"
    >
      <StatusMark tone={tone} quick={quick} />
      <h2 className="text-xl font-bold text-foreground">{title}</h2>
      {children ? <p className="max-w-sm text-sm text-muted-foreground">{children}</p> : null}
      {action}
    </div>
  );
}

type StatusTone = "success" | "error" | "pending";

/** The tick draws itself; the cross draws itself; pending is a turning arc. */
export function StatusMark({ tone, quick }: { tone: StatusTone; quick?: boolean }) {
  if (tone === "pending") {
    return (
      <svg viewBox="0 0 64 64" className="h-16 w-16 motion-safe:animate-spin" aria-hidden="true">
        <circle cx="32" cy="32" r="28" fill="none" strokeWidth="3" className="stroke-border" />
        <path
          d="M32 4 a28 28 0 0 1 28 28"
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
          className="stroke-primary"
        />
      </svg>
    );
  }
  return (
    <div className={cn("relative", quick && "login-mark-quick")}>
      {tone === "success" ? (
        <span
          aria-hidden="true"
          className="login-check-halo absolute inset-0 rounded-full bg-success/40"
        />
      ) : null}
      <svg viewBox="0 0 64 64" className="relative h-16 w-16" aria-hidden="true">
        <circle
          cx="32"
          cy="32"
          r="28"
          className={cn("login-check-circle", tone === "error" && "login-check-circle--error")}
        />
        {tone === "success" ? (
          <path d="M20 33 L28 41 L44 23" className="login-check-mark" />
        ) : (
          <path d="M24 24 L40 40 M40 24 L24 40" className="login-cross-mark" />
        )}
      </svg>
    </div>
  );
}

/** Three slow light orbs and a grain layer behind everything. Decorative. */
function AmbientBackdrop() {
  return (
    <div aria-hidden="true" className="login-ambient">
      <div className="login-orb login-orb--a" />
      <div className="login-orb login-orb--b" />
      <div className="login-orb login-orb--c" />
      <div className="login-grain" />
    </div>
  );
}

function BrandRow() {
  // The Royal mark is a multi-colour raster, so each theme gets its own
  // PNG; the Mount Sinai wordmark is an inline SVG in `currentColor`,
  // which takes the theme's text colour by itself.
  const { resolved } = useTheme();
  const royalSrc = resolved === "light" ? "/royal-logo-light.png" : "/royal-logo-black.png";
  return (
    // One name for the pair, as WorkspaceHeader's home link has: the
    // Mount Sinai wordmark is hidden from screen readers.
    <div
      role="img"
      aria-label="Royal Transportation and Mount Sinai Medical Center"
      className="flex items-center gap-3"
    >
      <img src={royalSrc} alt="Royal Transportation" className="h-12 w-auto 2xl:h-14" />
      <span className="text-base font-bold text-muted-foreground">×</span>
      <MountSinaiLogo className="h-9 w-auto text-foreground/90 2xl:h-11" />
    </div>
  );
}

// =============================================================================
// Hero pane (right)
// =============================================================================

/** A 24px preview of the hero photo, inlined so it paints with the page. It is
 *  shown blurred until the real photo has downloaded, so a slow hospital
 *  network sees a soft image instead of an empty panel. Regenerate it if the
 *  photo changes. */
const HERO_PLACEHOLDER =
  "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAA0JCgsKCA0LCgsODg0PEyAVExISEyccHhcgLikxMC4pLSwzOko+MzZGNywtQFdBRkxOUlNSMj5aYVpQYEpRUk//2wBDAQ4ODhMREyYVFSZPNS01T09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT0//wAARCAAgABgDASIAAhEBAxEB/8QAGgAAAgIDAAAAAAAAAAAAAAAAAAYDBQECBP/EACcQAAEEAQMCBgMAAAAAAAAAAAECAwQRAAUSIRMxBhQiQVFxMkJS/8QAFwEBAQEBAAAAAAAAAAAAAAAAAwECBP/EABoRAAIDAQEAAAAAAAAAAAAAAAABAhEhEjH/2gAMAwEAAhEDEQA/ALiHqDrmr+QfhmPSe61jk2a2/wBXx2+cmk6zCiS1x30ubk/slNg5leptyVuoYUlsAgHqAbuw7ZSai02qcdxIpIF0awFPaOhxdDHp8yPqXU8ule1ugSoVd4ZH4WbYZMgKcSAsJIJNX3+cMqd6jLz0RkS6fI1CSoIe9BQigk32JPNZ2Ifgx1rjIdLZQPWbCR9BXuRissJc/LnN22mxXpHHzzicksaW5ceW8GIxLiWqV1N91fsOPrDKrTlpS6OaT74YM4qx4PD/2Q==";

/** Below 1024px the hero panel is hidden, and a hidden <img> is still
 *  downloaded. Phones get this transparent pixel instead of the photo. */
const TRANSPARENT_PIXEL =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/** Set once the hero has made its entrance in this visit. Moving between
 *  sign-in pages (login → forgot password → back) re-mounts the pane, and
 *  the photo is already on screen, so it doesn't wipe in again. */
let heroEntrancePlayed = false;

function HeroPane() {
  const [settled] = useState(() => heroEntrancePlayed);
  useEffect(() => {
    heroEntrancePlayed = true;
  }, []);
  const imgRef = useRef<HTMLImageElement>(null);
  const [photoReady, setPhotoReady] = useState(settled);
  useEffect(() => {
    // A cached photo can finish loading before React attaches onLoad.
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 1) setPhotoReady(true);
  }, []);
  return (
    // The hero is a dark photograph with a navy gradient on top —
    // the headline + trust badges sit on a deliberately dark surface
    // in both themes. Lock the section to `data-theme="dark"` so
    // CSS variables resolve as if dark mode regardless of the user's
    // pick. Without this, `text-foreground/90` (a tint of the
    // *active* theme's foreground) would render navy-on-photo for
    // a light-theme viewer, which is unreadable.
    //
    // Rounded on the LEFT only, so the photo reads as a panel set into the
    // page rather than the screen cut in half. The right edge stays flush.
    <section
      aria-hidden="true"
      data-theme="dark"
      data-settled={settled || undefined}
      className="login-hero-reveal relative isolate hidden overflow-hidden lg:block lg:rounded-l-[2.75rem]"
    >
      {/* Hero photograph — the Royal Sienna at Mount Sinai. Two layers so the
          one-shot settle and the endless drift never fight over transform. */}
      <div className="login-photo-settle absolute inset-0">
        <div
          aria-hidden="true"
          className="absolute inset-0 scale-110 blur-2xl"
          style={{
            backgroundImage: `url(${HERO_PLACEHOLDER})`,
            backgroundSize: "cover",
            backgroundPosition: "center bottom",
          }}
        />
        {/* Anchored to the BOTTOM: the photo is composed with clean asphalt
            along its lower edge for the headline, so the Royal decal on the
            van door stays clear of the text on every screen size. The -v2
            name busts browser caches of the previous photo. */}
        <picture>
          <source media="(min-width: 1024px)" srcSet="/login-hero-v2.jpg" />
          <img
            ref={imgRef}
            src={TRANSPARENT_PIXEL}
            alt=""
            decoding="async"
            className={cn(
              "login-kenburns absolute inset-0 h-full w-full object-cover",
              !settled && "transition-opacity duration-700 motion-reduce:transition-none",
              photoReady ? "opacity-100" : "opacity-0",
            )}
            style={{ objectPosition: "center bottom" }}
            onLoad={(e) => {
              if (e.currentTarget.naturalWidth > 1) setPhotoReady(true);
            }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        </picture>
      </div>
      {/* Soft brand-tinted wash so the photo reads as part of the app
          palette rather than a stock photo dropped on the page. */}
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(225_58%_18%)]/55 via-transparent to-[hsl(258_75%_25%)]/45 mix-blend-multiply" />
      {/* Bottom-up readability gradient: keeps the headline + badges on
          deep navy, fades to roughly transparent in the upper third. */}
      <div className="absolute inset-0 bg-gradient-to-t from-[hsl(222_60%_8%)]/95 from-0% via-[hsl(222_60%_8%)]/55 via-45% to-transparent to-75%" />
      {/* Extra shade under the copy only, so the headline stays crisp where it
          crosses the white van on smaller screens, without dimming the rest
          of the photo. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 70% at 0% 100%, hsl(222 60% 8% / 0.7), transparent 60%)",
        }}
      />

      {/* The copy stays in the LEFT half of the photo, sized to the panel
          rather than the viewport: the Royal decal sits on the van's door,
          right of centre, and a full-width headline covered it. */}
      <div className="relative flex h-full flex-col justify-end gap-6 p-10 xl:p-14">
        <h2
          className="login-rise max-w-[min(20ch,48%)] text-[clamp(1.75rem,2.5vw,2.75rem)] font-extrabold leading-[1.1] tracking-tight text-white drop-shadow-[0_2px_18px_rgba(0,0,0,0.55)]"
          style={rise(900)}
        >
          Reliable transport for every{" "}
          <span className="whitespace-nowrap">
            <span className="login-gradient-text">Mount Sinai</span>{" "}
            <span className="text-white">patient.</span>
          </span>
        </h2>
        <p
          className="login-rise max-w-[min(44ch,52%)] text-base leading-relaxed text-foreground/90 drop-shadow-[0_1px_8px_rgba(0,0,0,0.45)]"
          style={rise(1020)}
        >
          One screen for booking trips, scheduling appointments, and keeping patient records
          consistent across every ride.
        </p>
        <div className="login-rise flex flex-wrap gap-2" style={rise(1140)}>
          <TrustBadge>HIPAA Compliant</TrustBadge>
          <TrustBadge>BAA with Google Cloud</TrustBadge>
          <TrustBadge>Encrypted at rest</TrustBadge>
        </div>
      </div>
    </section>
  );
}

function TrustBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-foreground/30 bg-foreground/[0.10] px-3 py-1.5 text-xs font-semibold text-white backdrop-blur transition duration-300 hover:-translate-y-0.5 hover:bg-foreground/[0.16]">
      <ShieldCheck className="h-3.5 w-3.5" />
      {children}
    </span>
  );
}
